import {isNil} from 'lodash';
import {
  forwardRef,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useInView} from 'react-intersection-observer';
import {SchemeLink} from '../../../applink/SchemeLink';
import {colorToRgba} from '../../../common/colorToRgba';
import {useShareForwardedRef} from '../../../common/shared/hooks/useShareForwardedRef';
import {twMerge} from '../../../common/twMerge';
import {BannerType, BannerViewType} from '../../../graphql/generated/types';
import {MOBILE_HEADER_HEIGHT_PX} from '../../../header/constants';
import {Image} from '../../atoms/Image/Image';
import {MOBILE_MAIN_TAB_HEIGHT_PX} from '../MainTab/constants';
import {PC_HEADER_HEIGHT} from '../PcHeader/constants';
import {BannerItemButton} from './BannerItemButton/BannerItemButton';
import {BannerItemInfo} from './BannerItemInfo/BannerItemInfo';
import {BannerItemProps} from './BannerItemProps.interface';
import {BIG_BANNER_RATIO} from './constants';

export const BannerItem = forwardRef<HTMLVideoElement, BannerItemProps>(
  (
    {
      bannerUid,
      viewType,
      bannerImageSrc,
      bannerVideoSrc,
      badgeList: descBadges,
      statusBadge,
      statusLabelText,
      metaList: descTexts,
      titleText,
      altText,
      titleImageSrc,
      caption,
      bannerType,
      isSelected,
      isStandaloneBanner,
      scheme,
      onVideoEnded,
      onVideoStart,
      isMuted = true,
      onVideoMutedChange,
      isMobile,
      isFoldable,
      onClickPlayVideo,
      isParentCarouselHasIndicator = false,
      className,
      discountRateText,
      backgroundColor: _backgroundColor,
      characterImageSrc,
      ...props
    },
    ref
  ) => {
    const [isPlaying, setIsPlaying] = useState(false);

    const onVideoEvent = useCallback(
      (e: SyntheticEvent<HTMLVideoElement>) => {
        const eventType = e.type;
        if (eventType === 'pause') {
          setIsPlaying(false);
          onVideoEnded?.();
        }
        if (eventType === 'play') {
          setIsPlaying(true);
          onVideoStart?.();
        }
      },
      [onVideoEnded, onVideoStart]
    );

    const videoRef = useShareForwardedRef(ref);

    const {ref: viewRef, inView} = useInView({
      rootMargin: `-${
        isMobile
          ? MOBILE_HEADER_HEIGHT_PX + MOBILE_MAIN_TAB_HEIGHT_PX
          : PC_HEADER_HEIGHT
      }px 0px 0px 0px`,
      threshold: 0.9,
    });

    const stopVideo = useCallback(() => {
      if (!videoRef.current || videoRef.current.paused) {
        return;
      }
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }, [videoRef]);

    useEffect(() => {
      if (!inView) {
        stopVideo();
      }
    }, [inView, stopVideo]);

    useEffect(() => {
      if (!isPlaying) {
        return;
      }
      function stopVideoIfVideoIsNotInView() {
        if (!inView) {
          stopVideo();
        }
      }
      window.addEventListener('scroll', stopVideoIfVideoIsNotInView);
      return () =>
        window.removeEventListener('scroll', stopVideoIfVideoIsNotInView);
    }, [inView, isPlaying, stopVideo]);

    useEffect(() => {
      if (!inView && videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }, [inView, videoRef]);

    useEffect(() => {
      if (!isSelected && videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
        videoRef.current.muted;
      }
    }, [isSelected, videoRef]);

    const shouldShowBadges = useMemo(
      () =>
        (descBadges && descBadges.length > 0) ||
        statusBadge ||
        statusLabelText ||
        (descTexts && descTexts.length > 0),
      [descBadges, descTexts, statusBadge, statusLabelText]
    );

    const setRefs = useCallback(
      (node: HTMLDivElement) => {
        viewRef(node);
      },
      [viewRef]
    );

    const onlyPcTopBannerStyle = useCallback(() => {
      // 로딩 시 탑배너 높이를 전체 너비의 100%로 잡아버려서 PC는 956px의 고정 너비를 기반으로 1:1 탑배너의 높이는 460px로 고정합니다.
      // isTopBanner 옵션을 BannerItem에서는 알 수 없으므로, isParentCarouselHasIndicator 유무로 판단합니다. (PC에서 ResponsiveBanner 배너는 인디케이터가 없습니다.)
      if (!isMobile && viewType === BannerViewType.Square) return 'h-[460px]';
    }, [isMobile, viewType]);

    const backgroundColor = isNil(_backgroundColor)
      ? '#333333' // TODO: @Root-huh 컬러 토큰 값 수정되면 rgb(var(--...)) 으로 수정하기
      : _backgroundColor;

    return (
      <div className="relative">
        <SchemeLink scheme={scheme || undefined} draggable={false}>
          <div
            ref={setRefs}
            // eslint-disable-next-line tailwindcss/no-custom-classname
            className={twMerge(
              isStandaloneBanner && 'banner-item-container',
              'relative isolate h-full w-full overflow-hidden rounded-16pxr',
              isStandaloneBanner &&
                'flex items-start justify-center before:absolute before:left-0 before:top-0 before:z-[-1] before:h-full before:w-full before:bg-cover before:bg-center before:bg-no-repeat before:opacity-40 before:blur-20px',
              onlyPcTopBannerStyle(),
              className
            )}
            {...props}
          >
            <Image
              // standaloneBanner의경우 width뿐만 아니라 height로도 요소가 통제되므로 aspect ratio box를 쓰기에 적합하지 않습니다.
              // (aspect ratio box는 무조건 width 기준으로 height 제한없이 영역을 잡기 때문)
              // 그래서 이 경우 ratio를 0으로 줘서 이미지 비율을 그대로 노출해줍니다.
              ratio={isStandaloneBanner ? 0 : BIG_BANNER_RATIO[viewType]}
              className={twMerge(
                'max-h-full object-contain',
                !isStandaloneBanner && 'w-full'
              )}
              src={bannerImageSrc}
              alt={altText || '배너 이미지'}
              draggable={false}
            />

            {bannerType === BannerType.Video && (
              <video
                className={twMerge(
                  'absolute inset-y-0 h-full w-full object-cover',
                  !isPlaying && 'invisible'
                )}
                src={bannerVideoSrc || undefined}
                muted={isMuted}
                onVolumeChange={onVideoEvent}
                onPause={onVideoEvent}
                onPlay={onVideoEvent}
                ref={videoRef}
                playsInline
                aria-hidden={true}
              />
            )}
            {characterImageSrc && (
              <Image
                ratio={isStandaloneBanner ? 0 : BIG_BANNER_RATIO[viewType]}
                src={characterImageSrc}
                className={twMerge(
                  'absolute top-0 left-0 max-h-full object-contain',
                  !isStandaloneBanner && 'w-full'
                )}
                alt="대표 이미지"
              />
            )}
            <div
              className="absolute bottom-0 left-0 w-full h-1/2"
              style={{
                background: `linear-gradient(180deg, 
                  ${colorToRgba(backgroundColor, 0)} 0%,
                  ${colorToRgba(backgroundColor, 0.5)} 50%, 
                  ${colorToRgba(backgroundColor, 0.7)} 100%)`,
              }}
            />
            {isSelected && (
              <BannerItemInfo
                isParentCarouselHasIndicator={isParentCarouselHasIndicator}
                shouldShowBadges={shouldShowBadges}
                isPlaying={isPlaying}
                titleImageSrc={titleImageSrc}
                titleText={titleText}
                caption={caption}
                statusBadge={statusBadge}
                statusLabelText={statusLabelText}
                descBadges={descBadges}
                descTexts={descTexts}
                aria-hidden={true}
                discountRateText={discountRateText}
              />
            )}
            {isSelected && (
              <BannerItemButton
                isParentCarouselHasIndicator={isParentCarouselHasIndicator}
                bannerUid={bannerUid}
                bannerType={bannerType}
                isPlaying={isPlaying}
                isMuted={isMuted}
                onVideoMutedChange={onVideoMutedChange}
                onClickPlayVideo={onClickPlayVideo}
              />
            )}
          </div>
        </SchemeLink>
        <style jsx>
          {`
            .banner-item-container::before {
              background-image: url(${bannerImageSrc});
            }
          `}
        </style>
      </div>
    );
  }
);
